import { useEffect, useMemo, useState, useRef } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import KPICard from "@/components/KPICard";
import { fmtMoney, fmtDate, titleCase } from "@/lib/format";
import { Plus, Receipt, Fuel, Wrench, Building2, Search, Trash2, Camera, Paperclip, Sparkles, Trash } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_META = {
    fuel:          { label: "Fuel",          icon: Fuel,      color: "text-orange-500" },
    maintenance:   { label: "Maintenance",   icon: Wrench,    color: "text-blue-500" },
    repairs:       { label: "Repairs",       icon: Wrench,    color: "text-red-500" },
    tires:         { label: "Tires",         icon: Wrench,    color: "text-yellow-600" },
    insurance:     { label: "Insurance",     icon: Building2, color: "text-indigo-500" },
    registration:  { label: "Registration",  icon: Building2, color: "text-indigo-400" },
    wages:         { label: "Wages",         icon: Building2, color: "text-emerald-500" },
    cart_purchase: { label: "Cart purchase", icon: Building2, color: "text-teal-500" },
    overhead:      { label: "Overhead",      icon: Building2, color: "text-slate-500" },
    office:        { label: "Office",        icon: Building2, color: "text-slate-400" },
    software:      { label: "Software",      icon: Building2, color: "text-violet-500" },
    other:         { label: "Other",         icon: Receipt,   color: "text-muted-foreground" },
};

const CATEGORIES = Object.keys(CATEGORY_META);

const emptyForm = () => ({
    category: "fuel", amount: "", description: "", vendor: "",
    truck_id: "none", gallons: "", odometer: "",
    expense_date: new Date().toISOString().slice(0, 10),
    receipt_file: null,
});

export default function Expenses() {
    const [expenses, setExpenses] = useState([]);
    const [summary, setSummary] = useState(null);
    const [trucks, setTrucks] = useState([]);
    const [dumpLoads, setDumpLoads] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [drawer, setDrawer] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [ocrBusy, setOcrBusy] = useState(false);
    const [ocrHint, setOcrHint] = useState(null);
    const fileRef = useRef(null);

    const runOcr = async (file) => {
        setOcrBusy(true);
        setOcrHint(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/expenses/parse-receipt", fd);
            setForm((f) => ({
                ...f,
                category: (f.category && f.category !== "fuel") ? f.category : (data.category_guess || f.category),
                amount: f.amount || (data.total_amount != null ? String(data.total_amount) : ""),
                gallons: f.gallons || (data.gallons != null ? String(data.gallons) : ""),
                odometer: f.odometer || (data.odometer != null ? String(data.odometer) : ""),
                vendor: f.vendor || data.vendor || "",
                // Only overwrite date if it's still the default (today) and OCR found one
                expense_date: (f.expense_date === new Date().toISOString().slice(0, 10) && data.receipt_date)
                              ? data.receipt_date
                              : f.expense_date,
                receipt_file: file,
            }));
            setOcrHint(data.confidence || "low");
            if (data.total_amount != null || data.vendor) {
                toast.success(`Auto-filled from receipt (${data.confidence})`);
            } else {
                toast.info("Receipt attached — please fill in the details");
            }
        } catch {
            setForm((f) => ({ ...f, receipt_file: file }));
            toast.info("Receipt attached — please fill in the details");
        } finally {
            setOcrBusy(false);
        }
    };

    const load = async () => {
        const [exp, sum, tk, dl] = await Promise.all([
            api.get("/expenses"),
            api.get("/expenses/summary"),
            api.get("/fleet/trucks"),
            api.get("/expenses/dump-loads", { params: { days: 30 } }),
        ]);
        setExpenses(exp.data);
        setSummary(sum.data);
        setTrucks(tk.data);
        setDumpLoads(dl.data);
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        return expenses.filter((e) => {
            if (categoryFilter !== "all" && e.category !== categoryFilter) return false;
            if (!search) return true;
            const blob = `${e.description || ""} ${e.vendor || ""} ${e.category}`.toLowerCase();
            return blob.includes(search.toLowerCase());
        });
    }, [expenses, categoryFilter, search]);

    const monthTotal = summary?.total || 0;
    const byCat = summary?.by_category || {};
    const fuelTotal = byCat.fuel || 0;
    const maintenanceTotal = (byCat.maintenance || 0) + (byCat.repairs || 0) + (byCat.tires || 0);
    const overheadTotal = (byCat.overhead || 0) + (byCat.office || 0) + (byCat.software || 0) + (byCat.insurance || 0);

    const submit = async () => {
        if (!form.amount || Number(form.amount) <= 0) {
            return toast.error("Enter an amount greater than zero");
        }
        setSaving(true);
        try {
            let receipt_file_id = null;
            if (form.receipt_file) {
                const fd = new FormData();
                fd.append("file", form.receipt_file);
                const { data } = await api.post("/uploads?kind=receipt", fd);
                receipt_file_id = data.id;
            }
            const payload = {
                category: form.category,
                amount: Number(form.amount),
                description: form.description,
                vendor: form.vendor,
                truck_id: form.truck_id === "none" ? null : form.truck_id,
                expense_date: form.expense_date,
                gallons: form.gallons ? Number(form.gallons) : null,
                odometer: form.odometer ? Number(form.odometer) : null,
                receipt_file_id,
            };
            await api.post("/expenses", payload);
            toast.success("Expense saved");
            setDrawer(false);
            setForm(emptyForm());
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save expense");
        } finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this expense?")) return;
        try {
            await api.delete(`/expenses/${id}`);
            toast.success("Deleted");
            await load();
        } catch {
            toast.error("Failed — admin only");
        }
    };

    return (
        <div data-testid="expenses-page">
            <PageHeader
                title="Expenses"
                subtitle="Fuel, maintenance, insurance, overhead — every dollar that leaves the business."
                actions={
                    <Button onClick={() => setDrawer(true)} data-testid="expense-add-btn">
                        <Plus className="w-4 h-4 mr-1" />Log expense
                    </Button>
                }
            />

            <div className="px-8 py-6 space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <KPICard testid="exp-kpi-total" label="Month total" value={fmtMoney(monthTotal)} icon={Receipt}
                             delta={summary?.month} />
                    <KPICard testid="exp-kpi-fuel" label="Fuel" value={fmtMoney(fuelTotal)} icon={Fuel}
                             delta={monthTotal ? `${Math.round((fuelTotal / monthTotal) * 100)}% of total` : "—"} />
                    <KPICard testid="exp-kpi-maint" label="Maintenance + Repairs" value={fmtMoney(maintenanceTotal)} icon={Wrench}
                             delta={monthTotal ? `${Math.round((maintenanceTotal / monthTotal) * 100)}% of total` : "—"} />
                    <KPICard testid="exp-kpi-overhead" label="Overhead + Office" value={fmtMoney(overheadTotal)} icon={Building2}
                             delta={monthTotal ? `${Math.round((overheadTotal / monthTotal) * 100)}% of total` : "—"} />
                    <KPICard
                        testid="exp-kpi-dump"
                        label="Dump fees (month)"
                        value={fmtMoney(dumpLoads?.month?.cost || 0)}
                        icon={Trash}
                        delta={dumpLoads?.month?.tons
                            ? `${dumpLoads.month.tons.toFixed(2)} t · ${dumpLoads.month.loads} loads`
                            : "No loads yet"}
                    />
                </div>

                {/* Dump loads panel — surfaces per-day tipping fees so the office
                    can spot changes without opening each route. */}
                <DumpLoadsPanel dumpLoads={dumpLoads} />

                <Card>
                    <CardContent className="p-4 flex flex-col md:flex-row gap-3 md:items-center">
                        <div className="flex items-center gap-2 flex-1">
                            <Search className="w-4 h-4 text-muted-foreground" />
                            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                                   placeholder="Search description, vendor, category…"
                                   data-testid="expense-search" />
                        </div>
                        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                            <SelectTrigger className="md:w-52" data-testid="expense-cat-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All categories</SelectItem>
                                {CATEGORIES.map((c) => (
                                    <SelectItem key={c} value={c}>{CATEGORY_META[c].label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-0">
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <div>
                                <div className="overline mb-1">Ledger</div>
                                <h3 className="font-heading text-lg font-semibold">Recent expenses</h3>
                            </div>
                            <div className="text-xs text-muted-foreground font-mono">{filtered.length} entries</div>
                        </div>
                        <div className="overflow-x-auto" data-testid="expense-list">
                            <table className="w-full text-sm">
                                <thead>
                                <tr className="border-b border-border surface">
                                    <th className="text-left px-4 py-3 overline">Date</th>
                                    <th className="text-left px-4 py-3 overline">Category</th>
                                    <th className="text-left px-4 py-3 overline">Description</th>
                                    <th className="text-left px-4 py-3 overline">Vendor</th>
                                    <th className="text-left px-4 py-3 overline">Truck</th>
                                    <th className="text-right px-4 py-3 overline">Amount</th>
                                    <th className="text-right px-4 py-3 overline"></th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.length === 0 && (
                                    <tr><td colSpan={7} className="text-center py-14 text-sm text-muted-foreground">
                                        No expenses yet — hit &ldquo;Log expense&rdquo; to add your first one.
                                    </td></tr>
                                )}
                                {filtered.map((e) => {
                                    const meta = CATEGORY_META[e.category] || CATEGORY_META.other;
                                    const Icon = meta.icon;
                                    const truck = trucks.find((t) => t.id === e.truck_id);
                                    return (
                                        <tr key={e.id} className="border-b border-border hover:bg-secondary/40"
                                            data-testid={`expense-row-${e.id}`}>
                                            <td className="px-4 py-3 whitespace-nowrap">{fmtDate(e.expense_date)}</td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-1.5 text-xs">
                                                    <Icon className={`w-3.5 h-3.5 ${meta.color}`} />{meta.label}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                {e.description || <span className="text-muted-foreground italic">—</span>}
                                                {e.gallons && <span className="ml-2 text-[11px] text-muted-foreground">({e.gallons} gal)</span>}
                                                {e.receipt_file_id && <Paperclip className="inline w-3 h-3 ml-1.5 text-muted-foreground" />}
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground">{e.vendor || "—"}</td>
                                            <td className="px-4 py-3 text-muted-foreground">{truck?.name || "—"}</td>
                                            <td className="px-4 py-3 text-right tabular font-semibold">{fmtMoney(e.amount)}</td>
                                            <td className="px-4 py-3 text-right">
                                                <Button size="sm" variant="ghost" onClick={() => remove(e.id)}
                                                        data-testid={`expense-delete-${e.id}`}>
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Sheet open={drawer} onOpenChange={setDrawer}>
                <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="expense-form-sheet">
                    <SheetHeader>
                        <SheetTitle className="font-heading text-xl">Log expense</SheetTitle>
                        <SheetDescription>Every fuel fill-up, repair, insurance premium counts.</SheetDescription>
                    </SheetHeader>
                    <div className="mt-6 space-y-4">
                        <div>
                            <Label className="text-xs">Category</Label>
                            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                <SelectTrigger data-testid="expense-form-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map((c) => (
                                        <SelectItem key={c} value={c}>{CATEGORY_META[c].label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Amount ($)</Label>
                                <Input type="number" step="0.01" value={form.amount}
                                       onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                       placeholder="0.00" data-testid="expense-form-amount" />
                            </div>
                            <div>
                                <Label className="text-xs">Date</Label>
                                <Input type="date" value={form.expense_date}
                                       onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                                       data-testid="expense-form-date" />
                            </div>
                        </div>

                        {form.category === "fuel" && (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs">Gallons</Label>
                                    <Input type="number" step="0.01" value={form.gallons}
                                           onChange={(e) => setForm({ ...form, gallons: e.target.value })}
                                           data-testid="expense-form-gallons" />
                                </div>
                                <div>
                                    <Label className="text-xs">Odometer (mi)</Label>
                                    <Input type="number" value={form.odometer}
                                           onChange={(e) => setForm({ ...form, odometer: e.target.value })}
                                           data-testid="expense-form-odo" />
                                </div>
                            </div>
                        )}

                        <div>
                            <Label className="text-xs">Truck (optional)</Label>
                            <Select value={form.truck_id} onValueChange={(v) => setForm({ ...form, truck_id: v })}>
                                <SelectTrigger data-testid="expense-form-truck"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— No specific truck —</SelectItem>
                                    {trucks.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>{t.name} ({titleCase(t.type)})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="text-xs">Vendor</Label>
                            <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                                   placeholder="Shell, Ace Truck Repair, Progressive…" data-testid="expense-form-vendor" />
                        </div>

                        <div>
                            <Label className="text-xs">Description / notes</Label>
                            <Textarea value={form.description} rows={2}
                                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                                      placeholder="Diesel top-up, tire rotation, quarterly premium…"
                                      data-testid="expense-form-description" />
                        </div>

                        <div>
                            <Label className="text-xs">Receipt (photo or PDF)</Label>
                            <input type="file" accept="image/*,application/pdf" ref={fileRef}
                                   onChange={(e) => {
                                       const f = e.target.files?.[0];
                                       if (!f) return;
                                       // Only OCR image files (not PDFs)
                                       if (f.type.startsWith("image/")) runOcr(f);
                                       else setForm({ ...form, receipt_file: f });
                                   }}
                                   className="hidden" data-testid="expense-form-file" />
                            {form.receipt_file ? (
                                <div className="space-y-1">
                                    <div className="flex items-center justify-between px-3 py-2 border border-border rounded-md text-sm">
                                        <span className="truncate flex items-center gap-2">
                                            {ocrBusy ? <Sparkles className="w-3.5 h-3.5 animate-pulse text-violet-500" /> : <Camera className="w-3.5 h-3.5 text-muted-foreground" />}
                                            {ocrBusy ? "Reading receipt…" : form.receipt_file.name}
                                        </span>
                                        <Button size="sm" variant="ghost" disabled={ocrBusy}
                                                onClick={() => { setForm({ ...form, receipt_file: null }); setOcrHint(null); }}>Clear</Button>
                                    </div>
                                    {ocrHint && (
                                        <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                                            <Sparkles className="w-3 h-3 text-violet-500" />
                                            AI auto-filled from receipt ({ocrHint} confidence) — please review before saving.
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Button variant="outline" onClick={() => fileRef.current?.click()}
                                        className="w-full h-16 border-dashed" data-testid="expense-form-file-btn">
                                    <Camera className="w-4 h-4 mr-2" />Attach receipt
                                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-600 dark:text-violet-300 uppercase tracking-wider">AI</span>
                                </Button>
                            )}
                        </div>

                        <div className="pt-4 border-t border-border flex items-center gap-3">
                            <Button variant="outline" className="flex-1" onClick={() => setDrawer(false)}>Cancel</Button>
                            <Button className="flex-1" onClick={submit} disabled={saving}
                                    data-testid="expense-form-submit">
                                {saving ? "Saving…" : "Save expense"}
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}


/** Dump-loads (tipping fee) panel on the Expenses page. Pulls from
 *  ``/api/expenses/dump-loads`` which unwinds ``route.dump_loads[]``
 *  into a single sortable list plus per-day / rolling totals.
 *
 *  Shows two blocks:
 *    1. Three summary tiles: today · last 7 days · avg $/ton
 *    2. Recent loads table with route, facility, tons, $, driver
 */
function DumpLoadsPanel({ dumpLoads }) {
    if (!dumpLoads) return null;
    const today = dumpLoads.today || { cost: 0, tons: 0, loads: 0 };
    const week  = dumpLoads.last_7d || { cost: 0, tons: 0, loads: 0 };
    const avg   = dumpLoads.totals?.avg_cost_per_ton || 0;
    const loads = dumpLoads.loads || [];

    return (
        <div data-testid="dump-loads-panel" className="space-y-3">
            <div className="flex items-baseline justify-between">
                <div>
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Trash className="w-4 h-4 text-muted-foreground" />
                        Dump loads
                    </h2>
                    <p className="text-xs text-muted-foreground">
                        Tipping fees + tonnage from every route in the last 30 days.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="rounded-lg border border-border p-3 bg-secondary/20">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Today</div>
                    <div className="mt-1 font-heading text-xl font-bold tabular">{fmtMoney(today.cost)}</div>
                    <div className="text-[11px] text-muted-foreground">{today.loads} load{today.loads === 1 ? "" : "s"} · {today.tons.toFixed(2)} t</div>
                </div>
                <div className="rounded-lg border border-border p-3 bg-secondary/20">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Last 7 days</div>
                    <div className="mt-1 font-heading text-xl font-bold tabular">{fmtMoney(week.cost)}</div>
                    <div className="text-[11px] text-muted-foreground">{week.loads} load{week.loads === 1 ? "" : "s"} · {week.tons.toFixed(2)} t</div>
                </div>
                <div className="rounded-lg border border-border p-3 bg-secondary/20">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Avg cost / ton (30d)</div>
                    <div className="mt-1 font-heading text-xl font-bold tabular">{fmtMoney(avg)}</div>
                    <div className="text-[11px] text-muted-foreground">{dumpLoads.totals?.tons?.toFixed?.(2) || "0.00"} t total</div>
                </div>
            </div>

            {loads.length > 0 ? (
                <div className="rounded-md border border-border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="surface bg-secondary/40">
                            <tr className="border-b border-border">
                                <th className="text-left px-3 py-2 overline">When</th>
                                <th className="text-left px-3 py-2 overline">Route · Facility</th>
                                <th className="text-right px-3 py-2 overline">Tons</th>
                                <th className="text-right px-3 py-2 overline">Cost</th>
                                <th className="text-left px-3 py-2 overline">By</th>
                            </tr>
                        </thead>
                        <tbody data-testid="dump-loads-tbody">
                            {loads.slice(0, 25).map((l) => (
                                <tr key={l.id} className="border-b border-border last:border-b-0">
                                    <td className="px-3 py-2 text-muted-foreground text-xs whitespace-nowrap">
                                        {l.recorded_at ? new Date(l.recorded_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="font-medium">{l.route_name || titleCase(l.route_day || "route")}</div>
                                        {l.facility && <div className="text-xs text-muted-foreground truncate">{l.facility}</div>}
                                        {l.notes && <div className="text-xs text-muted-foreground truncate">{l.notes}</div>}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular">{l.weight_tons.toFixed(2)}</td>
                                    <td className="px-3 py-2 text-right tabular font-medium">{fmtMoney(l.cost)}</td>
                                    <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[160px]">{l.recorded_by}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {loads.length > 25 && (
                        <div className="px-3 py-2 text-xs text-muted-foreground bg-secondary/20 border-t border-border">
                            Showing latest 25 of {loads.length} loads in the last 30 days
                        </div>
                    )}
                </div>
            ) : (
                <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                    No dump loads recorded yet. Drivers log these on the route
                    complete screen — cost + weight get pushed here automatically.
                </div>
            )}
        </div>
    );
}
